import { useId, useState } from "react";
import { PASSWORD_RULES } from "../lib/passwordRules";

interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** `new-password` where one is being chosen, `current-password` when signing in. */
  autoComplete: "new-password" | "current-password";
  /** Show the policy live. Only the field where the password is *chosen* needs it. */
  showRules?: boolean;
}

/**
 * A password input that says what it wants and lets you see what you typed.
 *
 * Two findings in one component. The rules were never stated, so the first thing a new customer met
 * was Cognito's raw policy error (UX review F7); and there was no reveal toggle, which is the thing
 * "confirm password" exists to substitute for — on a phone, one-handed, retyping a password blind is
 * where signups are lost. `autoComplete` is passed through because none of these fields carried it,
 * so password managers never offered to save what people had just invented (F8).
 */
export function PasswordField({ id, label, value, onChange, autoComplete, showRules = false }: Props) {
  const [revealed, setRevealed] = useState(false);
  const hintId = useId();

  return (
    <>
      <label htmlFor={id}>{label}</label>
      <div className="password-field">
        <input
          id={id}
          type={revealed ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          minLength={8}
          aria-describedby={showRules ? hintId : undefined}
        />
        <button
          type="button"
          className="chip password-reveal"
          onClick={() => setRevealed((on) => !on)}
          aria-pressed={revealed}
        >
          {revealed ? "Hide" : "Show"}
        </button>
      </div>
      {showRules && (
        <ul className="password-rules" id={hintId}>
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(value);
            return (
              <li key={rule.label} className={met ? "met" : undefined}>
                {/* The tick is decorative -- the class and the text carry the meaning for a screen
                    reader, which would otherwise hear a bullet character and nothing else. */}
                <span aria-hidden="true">{met ? "✓" : "•"}</span> {rule.label}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
